import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { LockOpen } from '@mui/icons-material';
import { FormDialog } from './ui';
import { IntegrationPlatform } from '../services/integrationApi';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import { SETTINGS_ANCHORS } from './dataIngestion/skipReasonActions';

const ELIGIBLE: IntegrationPlatform[] = ['shopify', 'bigcommerce', 'lightspeed', 'lightspeed_ecom'];

export interface ProPublicSyncSettingsSectionProps {
  platform: IntegrationPlatform;
  allowProPublicSync: boolean;
  allowProPublicSyncAcknowledgedAt?: unknown;
  allowProPublicSyncAcknowledgedBy?: string;
  onAllowProChange: (value: boolean) => void;
}

function parseAckDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>;
    if (typeof rec.toDate === 'function') {
      const d = (rec.toDate as () => Date)();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    const seconds = typeof rec.seconds === 'number' ? rec.seconds : typeof rec._seconds === 'number' ? rec._seconds : null;
    if (seconds !== null) {
      const d = new Date(seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

function formatAckAt(value: unknown): string | null {
  const d = parseAckDate(value);
  if (!d) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Pro-only / restricted catalog products are skipped from public store sync unless this is enabled.
 * Turning it on requires an explicit acknowledgment (stored server-side with user id and time).
 */
const ProPublicSyncSettingsSection: React.FC<ProPublicSyncSettingsSectionProps> = ({
  platform,
  allowProPublicSync,
  allowProPublicSyncAcknowledgedAt,
  allowProPublicSyncAcknowledgedBy,
  onAllowProChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!ELIGIBLE.includes(platform)) return null;

  const ackLabel = formatAckAt(allowProPublicSyncAcknowledgedAt);
  const uidShort =
    typeof allowProPublicSyncAcknowledgedBy === 'string' && allowProPublicSyncAcknowledgedBy.length > 8
      ? `${allowProPublicSyncAcknowledgedBy.slice(0, 6)}…`
      : allowProPublicSyncAcknowledgedBy || '';

  return (
    <>
      <Paper variant="outlined" id={SETTINGS_ANCHORS.proPublicSync} sx={{ scrollMarginTop: 80 }}>
        <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOpen sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">
              {COPY.proOnly.title}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
            {COPY.proOnly.subtitle}
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {COPY.proOnly.toggleLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                {COPY.proOnly.toggleDesc}
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={allowProPublicSync}
              onChange={(e) => {
                const on = e.target.checked;
                if (on) {
                  setConfirmed(false);
                  setDialogOpen(true);
                } else {
                  onAllowProChange(false);
                }
              }}
            />
          </Box>
          {allowProPublicSync && ackLabel && (
            <Alert severity="info" sx={{ mt: 0.5, py: 0.5, borderRadius: 1, fontSize: '0.75rem' }}>
              Acknowledged {ackLabel}
              {uidShort ? ` · User ${uidShort}` : ''}
            </Alert>
          )}
        </Box>
      </Paper>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Allow Pro-only products in store sync"
        onSubmit={() => {
          onAllowProChange(true);
          setDialogOpen(false);
          setConfirmed(false);
        }}
        submitLabel="Enable and record acceptance"
        submitColor="warning"
        submitDisabled={!confirmed}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          Pro-only products are restricted in Motzu for a reason (dealer programs, MAP, or vendor rules). Pushing them
          to a <strong>public</strong> e-commerce storefront may violate agreements, law, or your own policies.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          By continuing, you state that you have the right to sell or advertise these products on this integration,
          and you <strong>accept responsibility</strong> for compliance. Motzu logs this acceptance with your account
          and a timestamp.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              I understand and accept responsibility for syncing Pro-only products to this store.
            </Typography>
          }
        />
      </FormDialog>
    </>
  );
};

export default ProPublicSyncSettingsSection;
